import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  ArrowUpRight,
  Scale,
  FileCheck,
  Lock,
} from "lucide-react";
import { BRAND, KYC_IMAGE, COMPLIANCE_POINTS } from "../lib/constants";

const POINT_ICONS = [Lock, Scale, FileCheck];

export default function Compliance() {
  return (
    <section
      id="compliance"
      data-testid="compliance-section"
      className="relative theme-section py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#9FE3FF]/40 to-transparent" />
      <div className="absolute -top-24 right-0 w-[360px] h-[360px] bg-[#003E8C] rounded-full blur-[180px] opacity-35" />

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Header — matches About / Services editorial pattern */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-14 sm:mb-16"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-px bg-[#9FE3FF]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#9FE3FF] font-bold">
              / Compliance
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tighter">
            KYC &amp; AML built in,
            <br />
            <span className="text-gradient-cyan italic">not bolted on.</span>
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mt-6">
            {BRAND.name} is designed around rigorous KYC and AML practices. We safeguard clients,
            support operators, and help teams stay within lawful and ethical operating limits.
          </p>
        </motion.div>

        {/* Body — vertical timeline + framed image (not side-by-side card) */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute left-[19px] top-8 bottom-8 w-px bg-gradient-to-b from-[#9FE3FF] via-[#00C2FF]/50 to-transparent hidden sm:block" />

            <div className="space-y-6 sm:space-y-8">
              {COMPLIANCE_POINTS.map((p, i) => {
                const Icon = POINT_ICONS[i] || ShieldCheck;
                return (
                  <motion.div
                    key={p.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08, duration: 0.45 }}
                    data-testid={`compliance-point-${i}`}
                    className="relative flex gap-5 sm:gap-6 pl-0 sm:pl-2"
                  >
                    <div className="relative z-10 shrink-0 w-10 h-10 rounded-full border border-[#9FE3FF]/40 bg-[#001a2e] flex items-center justify-center text-[#9FE3FF] shadow-[0_0_24px_rgba(159,227,255,0.15)]">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="feature-card rounded-2xl p-5 sm:p-6 flex-1 min-w-0 group">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-display text-base font-semibold theme-heading group-hover:text-[#9FE3FF] transition-colors">
                          {p.title}
                        </h3>
                        <span className="font-mono text-[10px] text-gray-600 tracking-widest">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 leading-relaxed">{p.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25 }}
              className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5"
            >
              <a
                href="#contact"
                data-testid="compliance-cta"
                className="btn-outline inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold shrink-0"
              >
                Learn about compliance
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <p className="text-xs text-gray-500 max-w-xs leading-relaxed">
                Technology vendor only — no trading, advisory, or client funds on our books.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img
                src={KYC_IMAGE}
                alt="KYC / AML Compliance"
                className="w-full aspect-[16/10] sm:aspect-[5/3] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-[#000000]/90 via-black/30 to-transparent" />
              <div className="absolute top-5 right-5 w-10 h-10 border-t-2 border-r-2 border-[#9FE3FF]" />
              <div className="absolute bottom-5 left-5 w-10 h-10 border-b-2 border-l-2 border-[#00C2FF]" />

              <div className="relative mt-4 mx-4 sm:mx-0 sm:absolute sm:bottom-8 sm:right-8 max-w-[280px] sm:mt-0">
                <div className="glass-panel rounded-xl p-4 sm:p-5 border-[#9FE3FF]/20">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-4 h-4 text-[#9FE3FF]" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9FE3FF]">
                      Verification stack
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {["Identity", "Source of funds", "Sanctions screening"].map((label) => (
                      <li
                        key={label}
                        className="flex items-center justify-between text-xs text-gray-300"
                      >
                        <span>{label}</span>
                        <span className="font-mono text-[10px] text-[#00C2FF] uppercase tracking-wider">
                          Pass
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Metric strip — distinct from checklist layout above */}
            <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-4 border-t border-white/10 pt-6">
              {[
                { v: "100%", l: "Partner vetting" },
                { v: "0", l: "Client funds held" },
                { v: "Global", l: "AML frameworks" },
              ].map((s) => (
                <div key={s.l} className="text-center sm:text-left">
                  <div className="font-display text-xl sm:text-2xl font-extrabold text-gradient-cyan">
                    {s.v}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1 font-mono">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
